import { useCallback } from "react";
import { useAuthentication } from "@iad-os/react-ghost-auth";
import { useConfigStore } from "../store/configStore.js";
import { ui } from "../store/uiStore.js";
import i18n from "../i18n/index.js";

// Gestione "sessione scaduta" (401): una sola volta, anche se molte chiamate
// falliscono insieme. Mostra un toast e reindirizza al login dopo qualche secondo.
let sessionExpiredHandled = false;

// Refresh silenzioso al 401: se esiste un refresh token valido ("ricordami"),
// rinnova il token SENZA toast/redirect. Le chiamate 401 concorrenti condividono
// lo stesso refresh (niente storm). Ritorna il nuovo access_token, o null se il
// refresh non è possibile (sessione davvero scaduta).
let refreshPromise: Promise<string | null> | null = null;
function silentRefresh(refreshToken?: () => Promise<{ access_token?: string }>): Promise<string | null> {
  if (typeof refreshToken !== "function") return Promise.resolve(null);
  if (!refreshPromise) {
    refreshPromise = refreshToken()
      .then((r) => r?.access_token ?? null)
      .catch(() => null);
    void refreshPromise.finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface ApiOptions {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
  /** "json" (default) o "blob" (download file/PDF). */
  responseType?: "json" | "blob";
  /** Non mostrare il toast d'errore automatico. */
  silent?: boolean;
  /** Non incrementare il loader globale (per polling/silenziose). */
  noLoader?: boolean;
  /** Interni: usati dal retry dopo refresh silenzioso. */
  __retried?: boolean;
  __accessToken?: string;
}

interface ApiError {
  message: string;
  status: number;
}

export function useApi() {
  const config = useConfigStore((s) => s.config);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { token, login, refreshToken } = useAuthentication() as any;

  const request = useCallback(
    async <T>(path: string, options: ApiOptions = {}): Promise<T> => {
      const baseUrl = config?.metadata.endpoint_api ?? "";
      const url = `${baseUrl}${path}`;
      const accessToken: string | undefined = options.__accessToken ?? token?.access_token;

      const headers: Record<string, string> = { ...options.headers };
      // Content-Type descrive il BODY della richiesta (non la risposta): impostalo ogni volta che
      // inviamo un body JSON, anche quando attendiamo un blob (es. PDF con mappa i18n nel body).
      if (options.body !== undefined && !headers["Content-Type"]) {
        headers["Content-Type"] = "application/json";
      }
      if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

      if (!options.noLoader) ui.start();
      try {
        const response = await fetch(url, {
          method: options.method ?? "GET",
          headers,
          body: options.body !== undefined ? JSON.stringify(options.body) : undefined
        });

        if (!response.ok) {
          // Token scaduto / non autorizzato.
          if (response.status === 401) {
            // 1) Prova un REFRESH silenzioso (se c'è "ricordami"/refresh token valido):
            //    rinnova il token e RIPROVA la richiesta, senza toast né redirect.
            if (!options.__retried) {
              const newAccess = await silentRefresh(refreshToken);
              if (newAccess) {
                return await request<T>(path, { ...options, __retried: true, __accessToken: newAccess });
              }
            }
            // 2) Refresh non possibile → sessione davvero scaduta: avvisa una volta e rimanda al login.
            if (!sessionExpiredHandled) {
              sessionExpiredHandled = true;
              ui.toast(i18n.t("common.sessionExpired"), "warning");
              setTimeout(() => {
                try {
                  login?.();
                } catch {
                  window.location.href = "/protected";
                }
              }, 2800);
            }
            throw { message: i18n.t("common.sessionExpired"), status: 401 } as ApiError;
          }

          const errorBody = await response.json().catch(() => ({ message: response.statusText }));
          const err: ApiError = {
            message: (errorBody as { message?: string }).message ?? response.statusText,
            status: response.status
          };
          if (!options.silent) ui.toast(err.message, "error");
          throw err;
        }

        if (options.responseType === "blob") return (await response.blob()) as unknown as T;
        if (response.status === 204) return undefined as T;
        return (await response.json()) as T;
      } finally {
        if (!options.noLoader) ui.stop();
      }
    },
    [config, token, login, refreshToken]
  );

  /** Apre in una nuova scheda il PDF restituito da un endpoint (GET/POST). */
  const openPdf = useCallback(
    async (path: string, options: ApiOptions = {}): Promise<void> => {
      const blob = await request<Blob>(path, { method: "POST", ...options, responseType: "blob" });
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, "_blank");
      // Rilascia l'URL dopo un po' (il tab lo ha già caricato).
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    },
    [request]
  );

  /** Genera il PDF e apre direttamente il dialogo di stampa (via iframe nascosto). */
  const printPdf = useCallback(
    async (path: string, options: ApiOptions = {}): Promise<void> => {
      const blob = await request<Blob>(path, { method: "POST", ...options, responseType: "blob" });
      const objectUrl = URL.createObjectURL(blob);
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.src = objectUrl;
      iframe.onload = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch {
          // Fallback: se la stampa diretta è bloccata, apri il PDF in una scheda.
          window.open(objectUrl, "_blank");
        }
      };
      document.body.appendChild(iframe);
      setTimeout(() => {
        iframe.remove();
        URL.revokeObjectURL(objectUrl);
      }, 60_000);
    },
    [request]
  );

  return { call: request, openPdf, printPdf };
}
